import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Library, ExternalLink, Loader2, Settings, X, LogIn,
  ArrowUpAZ, ArrowDownAZ, Calendar, FolderOpen, Heart, BookOpen, Send,
} from 'lucide-react';
import { apiFetch } from '@/hooks/useFetch';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { Header } from '@/components/Header';

/**
 * Public Resources page. Logged-in users see a list of admin-curated
 * external links grouped by category, with sort + filter controls.
 * Guests get a friendly "login or create a free account" gate.
 *
 * Resource clicks open in a new tab — they go to third-party sites.
 */

// Sort modes — A-Z is the default (matches the user request that
// new resources should drop into their correct alphabetical spot
// automatically). The "category" mode auto-enables grouping so all
// three "by category, A-Z, Z-A" options live in one dropdown.
const SORT_OPTIONS = [
  { value: 'name_asc',  labelEn: 'Name A → Z',   labelEs: 'Nombre A → Z',  icon: ArrowUpAZ },
  { value: 'name_desc', labelEn: 'Name Z → A',   labelEs: 'Nombre Z → A',  icon: ArrowDownAZ },
  { value: 'category',  labelEn: 'By category tag', labelEs: 'Por etiqueta de categoría', icon: FolderOpen },
  { value: 'date_desc', labelEn: 'Newest first', labelEs: 'Más nuevos primero', icon: Calendar },
  { value: 'date_asc',  labelEn: 'Oldest first', labelEs: 'Más antiguos primero', icon: Calendar },
  { value: 'favorites', labelEn: 'My favorites first', labelEs: 'Mis favoritos primero', icon: Heart },
];

const DEFAULT_SORT_MODE = 'name_asc';
const VALID_SORT_MODES = new Set(SORT_OPTIONS.map((s) => s.value));

// Read filter state from the current URL so /resources?sort=...&cat=...
// is shareable + bookmarkable. Defaults match the page defaults so a
// bare `/resources` URL still works exactly as before.
function readFiltersFromUrl(search) {
  const p = new URLSearchParams(search);
  const sort = p.get('sort');
  return {
    search: p.get('q') || '',
    activeCategory: p.get('cat') || 'all',
    sortMode: sort && VALID_SORT_MODES.has(sort) ? sort : DEFAULT_SORT_MODE,
    favoritesOnly: p.get('fav') === '1',
  };
}

export default function ResourcesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  const { language } = useLanguage();
  const isSpanish = language === 'es';

  // Seed filter state from the URL so deep-linked filtered views land
  // pre-applied — friendly idea from the previous session, shipped.
  const initialFilters = readFiltersFromUrl(location.search);

  const [resources, setResources] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState(initialFilters.search);
  const [activeCategory, setActiveCategory] = useState(initialFilters.activeCategory);
  const [sortMode, setSortMode] = useState(initialFilters.sortMode);
  // Grouping is now driven by sortMode === 'category' (the dropdown's
  // "By category tag" option), so the standalone toggle was removed.
  const groupByCategory = sortMode === 'category';
  const [favoritesOnly, setFavoritesOnly] = useState(initialFilters.favoritesOnly);

  // Mirror filter state into the URL so it's shareable. We replace
  // (not push) so the back button still leaves the page rather than
  // unwinding through filter changes one at a time.
  useEffect(() => {
    const p = new URLSearchParams();
    if (search.trim()) p.set('q', search.trim());
    if (activeCategory !== 'all') p.set('cat', activeCategory);
    if (sortMode !== DEFAULT_SORT_MODE) p.set('sort', sortMode);
    if (favoritesOnly) p.set('fav', '1');
    const qs = p.toString();
    const next = qs ? `${location.pathname}?${qs}` : location.pathname;
    if (next !== `${location.pathname}${location.search}`) {
      navigate(next, { replace: true });
    }
  }, [search, activeCategory, sortMode, favoritesOnly, location.pathname, location.search, navigate]);

  const handleToggleFavorite = useCallback(async (resourceId) => {
    // Guests can't favorite — gracefully nudge them to sign in instead
    // of letting the optimistic flip + silent 401 confuse them.
    if (!isAuthenticated) {
      toast.info(isSpanish
        ? 'Inicia sesión para guardar favoritos'
        : 'Sign in to save favorites');
      navigate('/login?next=/resources');
      return;
    }
    // Optimistic flip
    setResources((prev) => prev.map((r) =>
      r.resource_id === resourceId ? { ...r, favorited: !r.favorited } : r
    ));
    const { error: err, data } = await apiFetch(
      `/api/resources/${resourceId}/favorite`,
      { method: 'POST' }
    );
    if (err) {
      // Roll back on failure
      setResources((prev) => prev.map((r) =>
        r.resource_id === resourceId ? { ...r, favorited: !r.favorited } : r
      ));
    } else if (data && typeof data.favorited === 'boolean') {
      // Reconcile with server's authoritative value
      setResources((prev) => prev.map((r) =>
        r.resource_id === resourceId ? { ...r, favorited: data.favorited } : r
      ));
    }
  }, [isAuthenticated, isSpanish, navigate]);

  // Load both endpoints in parallel for all users (guests included
  // since May 2026 — Resources is now public; only the favorite toggle
  // and the "Submit a Resource" form require authentication).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [resRes, catRes] = await Promise.all([
          apiFetch('/api/resources/list'),
          apiFetch('/api/resources/categories'),
        ]);
        if (cancelled) return;
        if (resRes.error || !resRes.data) {
          setError(resRes.error || 'Failed to load resources');
        } else {
          setResources(resRes.data.resources || []);
        }
        if (!catRes.error && catRes.data) {
          setAllCategories(catRes.data.categories || []);
        }
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Network error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filteredAndSorted = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = resources.filter((r) => {
      if (favoritesOnly && !r.favorited) return false;
      if (activeCategory !== 'all' && !(r.categories || []).includes(activeCategory)) {
        return false;
      }
      if (q) {
        const blob = `${r.name} ${r.description || ''} ${(r.categories || []).join(' ')}`.toLowerCase();
        if (!blob.includes(q)) return false;
      }
      return true;
    });
    list = [...list].sort((a, b) => {
      const aN = (a.name || '').toLowerCase();
      const bN = (b.name || '').toLowerCase();
      const aD = a.created_at || '';
      const bD = b.created_at || '';
      if (sortMode === 'favorites') {
        // Favorited first, then alphabetical within each group so the
        // user can still scan their favorites top-down predictably.
        if (!!a.favorited !== !!b.favorited) return a.favorited ? -1 : 1;
        return aN.localeCompare(bN);
      }
      if (sortMode === 'name_desc') return bN.localeCompare(aN);
      if (sortMode === 'date_asc')  return aD.localeCompare(bD);
      if (sortMode === 'date_desc') return bD.localeCompare(aD);
      // 'name_asc' (default) and 'category' (where items are also
      // alphabetical inside each category bucket) → A → Z by name.
      return aN.localeCompare(bN);
    });
    return list;
  }, [resources, search, activeCategory, sortMode, favoritesOnly]);

  const grouped = useMemo(() => {
    if (!groupByCategory) return null;
    const buckets = new Map();
    filteredAndSorted.forEach((r) => {
      const cats = r.categories?.length ? r.categories : ['Uncategorized'];
      cats.forEach((c) => {
        if (!buckets.has(c)) buckets.set(c, []);
        buckets.get(c).push(r);
      });
    });
    return Array.from(buckets.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filteredAndSorted, groupByCategory]);

  const clearFilters = useCallback(() => {
    setSearch('');
    setActiveCategory('all');
    setSortMode(DEFAULT_SORT_MODE);
    setFavoritesOnly(false);
  }, []);

  // Guest gate REMOVED May 2026 — Resources is now public. The
  // favorite toggle and "Submit a Resource" form are still
  // auth-gated inside the page (those interactions show an inline
  // "Sign in to use this" prompt instead of blocking the entire page).

  return (
    <>
      <Header />
      <div className="container mx-auto max-w-4xl px-4 py-6 sm:py-8" data-testid="resources-page">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
            <Library className="h-6 w-6 sm:h-7 sm:w-7 text-[#00A896]" />
            {isSpanish ? 'Recursos' : 'Resources'}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {isSpanish
              ? 'Enlaces seleccionados a herramientas y artículos útiles.'
              : 'Curated links to helpful tools and articles.'}
          </p>
        </div>
        {user?.is_admin && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/resources/manage')}
            data-testid="resources-manage-btn"
          >
            <Settings className="h-4 w-4 mr-1.5" />
            {isSpanish ? 'Administrar' : 'Manage'}
          </Button>
        )}
      </div>

      {/* Controls row */}
      <Card className="mb-4" data-testid="resources-controls">
        <CardContent className="p-3 sm:p-4 space-y-3">
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              placeholder={isSpanish ? 'Buscar…' : 'Search…'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1"
              data-testid="resources-search-input"
            />
            <Select value={activeCategory} onValueChange={setActiveCategory}>
              <SelectTrigger className="sm:w-[180px]" data-testid="resources-category-select">
                <SelectValue placeholder={isSpanish ? 'Todas las categorías' : 'All categories'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{isSpanish ? 'Todas las categorías' : 'All categories'}</SelectItem>
                {allCategories.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={sortMode} onValueChange={setSortMode}>
              <SelectTrigger className="sm:w-[170px]" data-testid="resources-sort-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SORT_OPTIONS.map(({ value, labelEn, labelEs }) => (
                  <SelectItem key={value} value={value}>{isSpanish ? labelEs : labelEn}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant={favoritesOnly ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFavoritesOnly((v) => !v)}
                data-testid="resources-favorites-toggle"
                className={favoritesOnly ? 'bg-pink-500 hover:bg-pink-600 text-white' : ''}
              >
                <Heart className={`h-4 w-4 mr-1.5 ${favoritesOnly ? 'fill-current' : ''}`} />
                {isSpanish ? 'Solo favoritos' : 'Favorites only'}
              </Button>
            </div>
            {(search || activeCategory !== 'all' || sortMode !== DEFAULT_SORT_MODE || favoritesOnly) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                data-testid="resources-clear-filters-btn"
              >
                <X className="h-4 w-4 mr-1" />
                {isSpanish ? 'Limpiar filtros' : 'Clear filters'}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Loading / error / empty states */}
      {loading && (
        <div className="flex items-center justify-center py-12 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" />
          {isSpanish ? 'Cargando…' : 'Loading…'}
        </div>
      )}
      {error && !loading && (
        <Card className="border-destructive/40 bg-destructive/5">
          <CardContent className="py-4 text-sm text-destructive" data-testid="resources-error">
            {error}
          </CardContent>
        </Card>
      )}
      {!loading && !error && filteredAndSorted.length === 0 && (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground" data-testid="resources-empty">
            {resources.length === 0
              ? (isSpanish ? 'Aún no hay recursos. Vuelve pronto.' : 'No resources yet. Check back soon.')
              : (isSpanish ? 'Nada coincide con tus filtros.' : 'Nothing matches your filters.')}
          </CardContent>
        </Card>
      )}

      {/* Grouped view */}
      {!loading && !error && groupByCategory && grouped && grouped.map(([cat, items]) => (
        <div key={cat} className="mb-6" data-testid={`resources-group-${cat}`}>
          <h2 className="text-lg font-semibold mb-2 text-[#00A896]">{cat}</h2>
          <div className="space-y-2">
            {items.map((r) => (
              <ResourceRow
                key={r.resource_id}
                resource={r}
                onToggleFavorite={handleToggleFavorite}
                isSpanish={isSpanish}
              />
            ))}
          </div>
        </div>
      ))}

      {/* Flat view */}
      {!loading && !error && !groupByCategory && filteredAndSorted.length > 0 && (
        <div className="space-y-2" data-testid="resources-list">
          {filteredAndSorted.map((r) => (
            <ResourceRow
              key={r.resource_id}
              resource={r}
              onToggleFavorite={handleToggleFavorite}
              isSpanish={isSpanish}
            />
          ))}
        </div>
      )}

      {/* Share-a-resource CTA — always visible at the bottom for
          logged-in users. Routes to /feedback?tab=submit-resource where
          the new "Submit a Resource" form lives. */}
      {!loading && !error && (
        <div className="mt-8" data-testid="resources-share-cta-wrap">
          <Card className="border-2 border-dashed border-[#00E5CC]/40 bg-gradient-to-br from-[#00E5CC]/5 to-purple-500/5">
            <CardContent className="p-4 sm:p-5 flex flex-col sm:flex-row items-center gap-3 sm:gap-4 text-center sm:text-left">
              <div className="flex-1">
                <p className="font-semibold text-sm sm:text-base">
                  {isSpanish ? '¿Conoces un recurso útil?' : 'Know of a resource?'}
                </p>
                <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                  {isSpanish
                    ? 'Compártelo con el Detective Dino para que investigue si lo añadimos a nuestra página.'
                    : 'Share with Detective Dino so he can investigate adding it to our page!'}
                </p>
              </div>
              <Button
                onClick={() => navigate('/feedback?tab=submit-resource')}
                className="bg-[#00C9B3] hover:bg-[#00B89F] text-[#0d1f2d] font-semibold shrink-0"
                data-testid="resources-share-cta-btn"
              >
                <Send className="h-4 w-4 mr-1.5" />
                {isSpanish ? 'Compartir un recurso' : 'Share a resource'}
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
    </>
  );
}

function ResourceRow({ resource, onToggleFavorite, isSpanish }) {
  return (
    <Card className="hover:shadow-md transition-shadow" data-testid={`resource-${resource.resource_id}`}>
      <CardContent className="p-3 sm:p-4">
        <div className="flex items-start justify-between gap-2">
          <a
            href={resource.url}
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-base sm:text-lg text-[#00A896] hover:underline inline-flex items-start gap-1.5 flex-1 min-w-0"
            data-testid={`resource-link-${resource.resource_id}`}
          >
            {/* Long resource titles (e.g. "Books for Neurodivergence &
                Chronic Illness", "Practice Cursive with these Books &
                Tools") must wrap, not truncate. Using `break-words` so
                very long single words break, but the line still wraps
                at natural spaces first. */}
            <span className="break-words flex-1 min-w-0">{resource.name}</span>
            <ExternalLink className="h-3.5 w-3.5 opacity-70 shrink-0 mt-1" />
          </a>
          <button
            type="button"
            onClick={() => onToggleFavorite(resource.resource_id)}
            aria-label={
              resource.favorited
                ? (isSpanish ? 'Quitar de favoritos' : 'Remove from favorites')
                : (isSpanish ? 'Agregar a favoritos' : 'Add to favorites')
            }
            aria-pressed={!!resource.favorited}
            className={`shrink-0 h-8 w-8 rounded-full inline-flex items-center justify-center transition-colors ${
              resource.favorited
                ? 'text-pink-500 hover:bg-pink-500/10'
                : 'text-muted-foreground hover:text-pink-500 hover:bg-pink-500/10'
            }`}
            data-testid={`resource-fav-${resource.resource_id}`}
          >
            <Heart className={`h-4 w-4 ${resource.favorited ? 'fill-current' : ''}`} />
          </button>
        </div>
        {resource.description && (
          <p className="text-sm text-muted-foreground mt-1 leading-snug break-words">
            {resource.description}
          </p>
        )}
        {resource.blog_post?.post_id && (
          /* Stack label + title vertically on narrow screens (block + inline
             text-flow) so the long blog title doesn't get pushed into a
             weird mis-aligned column to the right of the "Read our blog
             post:" label — which is what the user reported on mobile. */
          <a
            href={`/blog/${resource.blog_post.slug || resource.blog_post.post_id}`}
            className="block mt-2 text-xs text-[#7C3AED] hover:underline font-medium break-words"
            data-testid={`resource-blog-link-${resource.resource_id}`}
          >
            <BookOpen className="h-3.5 w-3.5 inline align-text-bottom mr-1 shrink-0" />
            <span className="opacity-90">
              {isSpanish ? 'Lee nuestro blog:' : 'Read our blog post:'}
            </span>
            {' '}
            <span className="underline-offset-2 break-words">
              {resource.blog_post.title}
            </span>
          </a>
        )}
        {resource.categories?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {resource.categories.map((c) => (
              <Badge key={c} variant="secondary" className="text-[10px] sm:text-xs">{c}</Badge>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
